"use client";

import { useMemo } from "react";
import { useUser } from "@clerk/nextjs";
import { useSearchParams } from "next/navigation";
import { ADMIN_VIEW_AS_EMAIL_PARAM } from "@/lib/admin-constants";
import { isManagerDashboardElevatedRole, isManagerDashboardRole, isSuperAdminRole } from "@/lib/roles";

function readUserRole(
  role: unknown,
): string | undefined {
  return typeof role === "string" && role.trim() !== "" ? role.trim() : undefined;
}

/**
 * Returns the user's role from Clerk publicMetadata (same source as timezone).
 */
export function useUserRole(): string | undefined {
  const { user, isLoaded } = useUser();
  return useMemo(
    () => (isLoaded && user ? readUserRole(user.publicMetadata?.role) : undefined),
    [user, isLoaded],
  );
}

/** Clerk user + whether publicMetadata.role is super_admin. */
export function useIsSuperAdmin() {
  const { user, isLoaded } = useUser();
  const role = useMemo(
    () => (isLoaded && user ? readUserRole(user.publicMetadata?.role) : undefined),
    [user, isLoaded],
  );
  const superAdmin = Boolean(isLoaded && user && isSuperAdminRole(role));

  return { user, isLoaded, role, superAdmin };
}

export function useIsManagerOrSuperAdmin() {
  const { user, isLoaded } = useUser();
  const role = useMemo(
    () => (isLoaded && user ? readUserRole(user.publicMetadata?.role) : undefined),
    [user, isLoaded],
  );
  const managerOrSuperAdmin = Boolean(
    isLoaded && user && isManagerDashboardRole(role),
  );

  return { user, isLoaded, role, managerOrSuperAdmin };
}

/** VP + super admin — elevated manager dashboard (all managers, URL filter). */
export function useHasManagerDashboardElevatedView() {
  const { user, isLoaded } = useUser();
  const role = useMemo(
    () => (isLoaded && user ? readUserRole(user.publicMetadata?.role) : undefined),
    [user, isLoaded],
  );
  const managerDashboardElevated = Boolean(
    isLoaded && user && isManagerDashboardElevatedRole(role),
  );

  return { user, isLoaded, role, managerDashboardElevated };
}

/**
 * Email used for meeting-prep API calls. Super admins with `view_as_email` in the URL
 * use that rep's email; everyone else uses their Clerk email.
 */
export function useMeetingPrepEmail() {
  const searchParams = useSearchParams();
  const { user, isLoaded, superAdmin } = useIsSuperAdmin();
  const clerkEmail = user?.primaryEmailAddress?.emailAddress;

  const viewAsEmail = useMemo(() => {
    if (!superAdmin) return undefined;
    const trimmed = searchParams.get(ADMIN_VIEW_AS_EMAIL_PARAM)?.trim();
    return trimmed || undefined;
  }, [searchParams, superAdmin]);

  const email = superAdmin && viewAsEmail ? viewAsEmail : clerkEmail;

  return { email, viewAsEmail, superAdmin, isLoaded };
}
